import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import UserCoworker from "users/information/coworker-information/user-coworker";
import {
  ProposalReviewerJSON,
  ProposalReviewerParams,
} from "work/proposals/reviewers/_schemas/proposal-reviewer-schema";

export default class ProposalReviewer {
  public static get EXAMPLE(): ProposalReviewer {
    return new ProposalReviewer({
      isEditing: true,
      entityMember: EntityMember.EXAMPLE,
      dateApproved: new AHDate(),
      customMessage: "Looking forward to working on this proposal!",
    });
  }

  public static fromJSON(json: ProposalReviewerJSON): ProposalReviewer {
    const entityMember = EntityMember.fromJSON(json);
    if (!entityMember) {
      throw new Error("Cannot create ProposalReviewer from JSON with invalid EntityMember");
    }
    return new ProposalReviewer({
      entityMember,
      isEditing: json.isEditing,
      dateApproved: json.dateApproved ? new AHDate(json.dateApproved) : undefined,
      customMessage: json.customMessage ?? undefined,
      isAwaitingReviewRequest: json.isAwaitingReviewRequest ?? undefined,
    });
  }

  public static createReviewerFromCoworker(
    coworker: UserCoworker,
    existingReviewer?: ProposalReviewer,
  ): ProposalReviewer {
    const entityMember = EntityMember.create({
      legalEntityId: coworker.legalEntityId,
      userId: coworker.userId,
    });

    if (existingReviewer) {
      return new ProposalReviewer({
        entityMember,
        isEditing: existingReviewer.isEditing,
        dateApproved: existingReviewer.dateApproved,
        customMessage: existingReviewer.customMessage,
        isAwaitingReviewRequest: existingReviewer.isAwaitingReviewRequest,
      });
    }

    return new ProposalReviewer({
      entityMember,
      isEditing: false,
    });
  }

  public readonly isEditing: boolean;
  public readonly entityMember: EntityMember;
  public readonly dateApproved?: AHDate;
  public readonly customMessage?: string;
  /**
   * On the roster and never asked. A reviewer carried on to a revision of a hired agreement opens in
   * this state: they reviewed the agreement being revised, so they belong here, but nobody has asked
   * them about the change and they have not been told one exists. It is what the reviewers popover's
   * Send offers to clear, and it is false for everybody else, who were asked when they were added.
   */
  public readonly isAwaitingReviewRequest: boolean;

  public get userId(): Guid {
    return this.entityMember.userId;
  }
  public get entityId(): Guid {
    return this.entityMember.legalEntityId;
  }

  constructor(params: ProposalReviewerParams) {
    const {
      isEditing,
      entityMember: entityMemberParams,
      dateApproved,
      customMessage,
      isAwaitingReviewRequest,
    } = params;
    this.isEditing = isEditing;
    this.entityMember = EntityMember.create({
      ...params,
      ...entityMemberParams,
    });
    this.dateApproved = dateApproved;
    this.customMessage = customMessage;
    this.isAwaitingReviewRequest = isAwaitingReviewRequest ?? false;
  }

  public isEqualTo = (other: ProposalReviewer): boolean =>
    this.entityMember.isEqualTo(other.entityMember) &&
    this.isEditing === other.isEditing &&
    (this.customMessage ?? "") === (other.customMessage ?? "");

  public clone(): this {
    return this;
  }

  public toJSON(): ProposalReviewerJSON {
    return {
      userId: this.entityMember.userId.toJSON(),
      legalEntityId: this.entityMember.legalEntityId.toJSON(),
      isEditing: this.isEditing,
      dateApproved: this.dateApproved?.toISOString(),
      customMessage: this.customMessage ?? "",
      isAwaitingReviewRequest: this.isAwaitingReviewRequest,
    };
  }
}
